import RCDrawer from '@rc-component/drawer';
import { clsx } from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getColorCSSVar } from '../../utils/colors';
import { shadowAppPortal } from '../../utils/shadowRoot';
import { Button } from '../button/button';
import { Flex } from '../flex/flex';
import { Modal } from '../modal/modal';
import { Typography } from '../typography/typography';
import { DrawerContext } from './drawer.context';
import type { DrawerProps } from './drawer.types';
import { DrawerContent } from './drawerContent/drawerContent';
import { DrawerContentDivider } from './drawerContent/drawerContentDivider';
import { DrawerFooter } from './drawerFooter/drawerFooter';
import { DrawerHeader } from './drawerHeader/drawerHeader';

import '@rc-component/drawer/assets/index.css';
import './animations.css';
import styles from './drawer.module.css';

const FULL_HEIGHT = '100vh';
const FULL_WIDTH = '100vw';

/**
 * `Drawer` component is a modal that slides from the side of the screen
 * with available headers and footers, or full-screen drawers.
 */
const BaseDrawer = ({
  'aria-labelledby': ariaLabelledBy = 'drawer-header-title',
  'data-testid': testId,
  allowScroll = false,
  background = 'grays-white',
  children,
  className,
  closeConfirmation = false,
  container,
  fitScreen = true,
  full = false,
  height = 'fit-content',
  hideOverlay = false,
  id,
  onRequestClose,
  open,
  overlayClose = true,
  placement = 'right',
  style,
  width = 'fit-content',
}: DrawerProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [closing, setClosing] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const horizontal = placement === 'left' || placement === 'right';

  const handleClose = useCallback(() => {
    setShowConfirmation(false);
    setClosing(true);
    if (!allowScroll) {
      document.body.style.overflow = 'unset';
    }
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      onRequestClose();
      setClosing(false);
    }, 475);
  }, [allowScroll, onRequestClose]);

  const onClose = useCallback(() => {
    if (closeConfirmation) {
      setShowConfirmation(true);
    } else {
      handleClose();
    }
  }, [closeConfirmation, handleClose, setShowConfirmation]);

  const drawerValue = useMemo(
    () => ({
      onRequestClose: onClose,
      fitScreen,
    }),
    [onClose, fitScreen]
  );

  useEffect(() => {
    if (open && !allowScroll) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      setClosing(false);
      clearTimeout(timeoutRef.current);
      if (!allowScroll) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [allowScroll, open]);

  const getSize = () => {
    if (horizontal) {
      return full ? FULL_WIDTH : width;
    }
    if (!fitScreen) {
      return undefined;
    }
    return full ? FULL_HEIGHT : height;
  };

  return (
    <DrawerContext.Provider value={drawerValue}>
      <RCDrawer
        aria-labelledby={ariaLabelledBy}
        data-testid={testId}
        id={id}
        rootClassName={clsx(className, styles['drawer'])}
        className={clsx(styles['rc-drawer-section'])}
        destroyOnHidden
        getContainer={
          container || (() => shadowAppPortal() || document.body) // SSR-safe lazy lookup
        }
        mask={!hideOverlay}
        maskClassName={clsx(styles['rc-drawer-mask'], {
          [styles['rc-drawer-mask--in']]: !closing,
          [styles['rc-drawer-mask--out']]: closing,
        })}
        maskClosable={overlayClose}
        onClose={onClose}
        open={open}
        placement={placement}
        size={getSize()}
        style={{
          '--background-color': getColorCSSVar(background),
          ...style,
        }}
        styles={{
          wrapper: {
            animation: `${
              closing
                ? `reefer-drawer-slide-out-${placement}`
                : `reefer-drawer-slide-in-${placement}`
            } 500ms cubic-bezier(0.16, 1, 0.3, 1)`,
            ...(placement === 'bottom' && { bottom: 0 }),
            zIndex: 'var(--layers-drawer)',
          },
        }}
      >
        {children}
      </RCDrawer>

      {!!closeConfirmation && (
        <Modal
          open={showConfirmation}
          onRequestClose={handleClose}
          variant="dialogue"
        >
          <Modal.Content>
            <Typography textAlign="center" variant="h2-semibold">
              Are you sure?
            </Typography>

            <Typography>{closeConfirmation}</Typography>

            <Flex gap={16} mt={24}>
              <Button
                label="Cancel"
                full
                variant="secondary"
                onClick={() => setShowConfirmation(false)}
              />

              <Button label="Confirm" full onClick={handleClose} />
            </Flex>
          </Modal.Content>
        </Modal>
      )}
    </DrawerContext.Provider>
  );
};

export const Drawer = Object.assign(BaseDrawer, {
  Header: DrawerHeader,
  Content: DrawerContent,
  ContentDivider: DrawerContentDivider,
  Footer: DrawerFooter,
});
